import {
  FC,
  ImgHTMLAttributes,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { GET_BASE_URL_IMAGE } from '../constants/apiEndpoints';

type Resort = 'disneyland' | 'disneyworld';

type ChatImageWithFallbackProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  'src'
> & {
  filename: unknown;
  resort?: Resort;
  preferredQuality?: 'original' | 'medium' | 'thumbnail';
};

type ChatImageVariant = {
  label: 'original' | 'medium' | 'thumbnail';
  url: string;
};

const CHAT_IMAGE_CACHE_VERSION = '20260617-orientation';

export function isChatImageFilename(filename: unknown): filename is string {
  return typeof filename === 'string' && filename.trim().includes('c_img');
}

function appendChatImageCacheVersion(url: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}mwv=${CHAT_IMAGE_CACHE_VERSION}`;
}

export function buildChatImageFallbackUrls(
  filename: unknown,
  resort: Resort = 'disneyland',
  preferredQuality: 'original' | 'medium' | 'thumbnail' = 'medium'
): string[] {
  return buildChatImageFallbackVariants(filename, resort, preferredQuality).map(
    (variant) => variant.url
  );
}

function buildChatImageFallbackVariants(
  filename: unknown,
  resort: Resort = 'disneyland',
  preferredQuality: 'original' | 'medium' | 'thumbnail' = 'medium'
): ChatImageVariant[] {
  if (!isChatImageFilename(filename)) {
    return [];
  }

  const safeFilename = filename.trim();
  const variants: ChatImageVariant[] = [
    {
      label: 'original',
      url: appendChatImageCacheVersion(
        `${GET_BASE_URL_IMAGE}/${resort}/chat_images/${safeFilename}`
      ),
    },
    {
      label: 'medium',
      url: appendChatImageCacheVersion(
        `${GET_BASE_URL_IMAGE}/${resort}/chat_images_medium/${safeFilename}`
      ),
    },
    {
      label: 'thumbnail',
      url: appendChatImageCacheVersion(
        `${GET_BASE_URL_IMAGE}/${resort}/chat_images_thumbnail/${safeFilename}`
      ),
    },
  ];

  const preferredIndex = variants.findIndex(
    (variant) => variant.label === preferredQuality
  );

  if (preferredIndex <= 0) {
    return variants;
  }

  return [
    ...variants.slice(preferredIndex),
    ...variants.slice(0, preferredIndex),
  ];
}

export const ChatImageWithFallback: FC<ChatImageWithFallbackProps> = ({
  filename,
  resort = 'disneyland',
  preferredQuality = 'medium',
  alt = '',
  onError,
  ...imgProps
}) => {
  const urls = useMemo(
    () => buildChatImageFallbackVariants(filename, resort, preferredQuality),
    [filename, resort, preferredQuality]
  );
  const [urlIndex, setUrlIndex] = useState(0);

  useEffect(() => {
    setUrlIndex(0);
  }, [urls]);

  if (urls.length === 0 || urlIndex >= urls.length) {
    return null;
  }

  const handleError = (event: SyntheticEvent<HTMLImageElement, Event>) => {
    onError?.(event);
    setUrlIndex((currentIndex) => currentIndex + 1);
  };

  return (
    <img
      {...imgProps}
      src={urls[urlIndex].url}
      alt={alt}
      data-mw-chat-image-quality={urls[urlIndex].label || 'fallback'}
      onError={handleError}
    />
  );
};
